import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Typography, Grid, Card, CardHeader, CardContent, Avatar, Button, Box } from '@mui/material';
import { menuItems, appHighlightItems } from '../helpers/MenuItems';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import PageLoading from '../helpers/PageLoading';
import { getCurrentUser } from '../api/services/userService/User';

const lightningKeyframes = `
@keyframes lightning {
  0% {
    filter: brightness(1);
    transform: scale(1);
  }
  25% {
    filter: brightness(1.5) contrast(1.5);
    transform: scale(1.1);
  }
  50% {
    filter: brightness(1.2);
    transform: scale(1);
  }
  75% {
    filter: brightness(1.4);
    transform: scale(1.05);
  }
  100% {
    filter: brightness(1);
    transform: scale(1);
  }
}`;

function getDescription(label) {
  switch (label) {
    case "Dashboard":
      return {
        main: "Real-time AI Operations Center",
        sub: "Monitor your AI platform activities, resource usage, system health, and get comprehensive analytics across all your AI workloads and deployments."
      };
    case "Models":
      return {
        main: "AI Model Management Hub",
        sub: "Import, version control, and organize your machine learning models. Supports popular frameworks like TensorFlow, PyTorch, Hugging Face, and custom models."
      };
    case "Training":
      return {
        main: "Advanced Model Training Pipeline",
        sub: "Launch, monitor, and control distributed training jobs with GPU acceleration. Track metrics, manage hyperparameters, and optimize training pipelines with automatic scaling."
      };
    case "Agent Studio":
      return {
        main: "No-Code AI Agent Builder",
        sub: "Create, deploy, and manage intelligent AI agents for your business. Build conversational AI, workflow automation, and custom AI solutions without coding."
      };
    case "Marketplace":
      return {
        main: "AI Application Marketplace",
        sub: "Discover and install pre-built AI applications, models, and solutions. Browse verified apps for computer vision, NLP, automation, and industry-specific use cases."
      };
    case "Cloud Management":
      return {
        main: "Multi-Cloud Infrastructure Management",
        sub: "Optimize cloud costs, manage Kubernetes clusters, ensure security compliance, and scale AI workloads across AWS, Azure, GCP, and on-premises infrastructure."
      };
    case "Test":
      return {
        main: "Test Environment",
        sub: "Sandbox environment for testing new features and configurations"
      };
    default:
      return { main: "", sub: "" };
  }
}

export default function Home() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const { keycloak, initialized } = useKeycloak();

  const fetchCurrentUser = async () => {
    try {
      await keycloak.updateToken(30);
      const response = await getCurrentUser(keycloak.token);

      if (response.success) {
        setCurrentUser(response.user);
      } else {
        console.error('Failed to fetch current user:', response.error);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      fetchCurrentUser();
    } else {
      setUserLoading(false);
    }
  }, [initialized, keycloak.authenticated]);



  if (!initialized) {
    return <PageLoading />;
  }

  const getUserDisplayName = () => {
    if (userLoading) return '';
    if (currentUser) {
      return currentUser.firstName && currentUser.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : currentUser.email || '';
    }
    return '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 3 } }}>
      <style>{lightningKeyframes}</style>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Welcome{getUserDisplayName() && `, ${getUserDisplayName()}`}
      </Typography>
      <Typography variant="subtitle1" gutterBottom sx={{
        fontSize: { xs: '0.9rem', sm: '1rem' },
        mb: { xs: 2, sm: 3 }
      }}>
        PioSphere is your comprehensive AI platform for the entire machine learning lifecycle. From model development to deployment, 
        training to monitoring, and everything in between - all in one unified platform.
      </Typography>
      
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }} sx={{ mt: 1 }}>
        {menuItems.slice(1).map((item) => {
          const IconComponent = item.icon;
          return (
            <Grid item xs={12} sm={6} md={6} lg={4} key={item.label}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: '200px', sm: '220px', md: '240px' },
                transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  transform: 'translateY(-4px)'
                }
              }}>
                <CardHeader
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          animation: 'lightning 0.5s ease-in-out',
                          bgcolor: 'primary.light',
                          '& .MuiSvgIcon-root': {
                            color: 'primary.main',
                            transform: 'scale(1.2)',
                          }
                        },
                        '& .MuiSvgIcon-root': {
                          transition: 'all 0.3s ease',
                          fontSize: { xs: '1.2rem', sm: '1.5rem' }
                        }
                      }}
                    >
                      {IconComponent ? <IconComponent /> : null}
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      fontWeight: 600
                    }}>
                      {item.label}
                    </Typography>
                  }
                  sx={{ pb: { xs: 1, sm: 2 } }}
                />
                <CardContent sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  pt: 0,
                  px: { xs: 2, sm: 2 },
                  pb: { xs: 2, sm: 2 }
                }}>
                  <Box>
                    <Typography variant="body1" color="text.primary" sx={{
                      mb: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: { xs: 1.4, sm: 1.5 },
                      fontWeight: 500
                    }}>
                      {getDescription(item.label).main}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      mb: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      lineHeight: { xs: 1.3, sm: 1.4 }
                    }}>
                      {getDescription(item.label).sub}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    endIcon={<ChevronRightIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                    fullWidth
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 1 }
                    }}
                    onClick={() => {
                      if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
                        window.open(item.path, '_blank');
                      } else {
                        navigate(item.path);
                      }
                    }}
                  >
                    Go to {item.label}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: { xs: 4, sm: 6 } }}>
        <Typography variant="h5" gutterBottom sx={{
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          fontWeight: 600
        }}>
          Platform Highlights
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{
          mb: { xs: 2, sm: 3 },
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}>
          Explore advanced features and specialized tools designed to accelerate your AI development workflow
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} sm={12} md={6}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: { xs: '180px', sm: '200px' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-6px)'
              }
            }}>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 }
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 700
                  }}>
                    🚀 Agent Studio
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 3,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 },
                    opacity: 0.9
                  }}>
                    Create powerful AI agents without coding. Drag, drop, and deploy intelligent solutions for customer service, automation, and business processes.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                  onClick={() => navigate('/agent-studio')}
                >
                  Launch Agent Studio
                </Button>

              </CardContent>
            </Card>
          </Grid>
                    
          <Grid item xs={12} sm={12} md={6}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: { xs: '180px', sm: '200px' },
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(79, 172, 254, 0.4)',
                transform: 'translateY(-6px)'
              }
            }}>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 }
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 700
                  }}>
                    🤖 PioAgent
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 3,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 },
                    opacity: 0.9
                  }}>
                    Advanced RAG-powered chatbot with retrieval-augmented generation for accurate, context-aware AI conversations and support.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                  onClick={() => window.open('https://pioagent.piosphere.ai', '_blank')}
                >
                  Launch PioAgent
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: { xs: 4, sm: 6 } }}>
        <Typography variant="h5" gutterBottom sx={{
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          fontWeight: 600
        }}>
          Why Choose PioSphere?
        </Typography>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                🏢 Enterprise-Ready
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Built for scale with enterprise security, compliance, and governance features. 
                Multi-tenant architecture with role-based access control and audit trails.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                ⚡ High Performance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optimized for AI workloads with GPU acceleration, distributed computing, 
                and intelligent resource management across multiple cloud providers.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                🔗 Seamless Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect with your existing tools and workflows. REST APIs, webhooks, 
                and pre-built connectors for popular ML frameworks and business systems.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                🎯 User-Friendly
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intuitive interface designed for both technical and non-technical users. 
                No-code solutions alongside powerful APIs for developers.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      
    </Box>
  );
} 